import { useEffect } from "react";
import { useTheme } from "next-themes";
import { useAuth } from "@/components/AuthProvider";
import { useAppearanceSettings } from "@/hooks/useAppearanceSettings";
import { applyDepth, clampDepth, readStoredDepth, storeDepth } from "@/lib/darkModeDepth";
const THEME_VALUES = new Set(["system", "light", "dark"]);

/**
 * Reconciles server-stored appearance preferences into the localStorage cache
 * on app load, so preferences follow the user across browsers. Renders
 * nothing; mounted once inside AuthProvider.
 */
export function AppearanceSettingsSync() {
  const {
    user
  } = useAuth();
  const {
    theme,
    setTheme
  } = useTheme();
  const {
    data
  } = useAppearanceSettings(!!user);

  // Re-apply the cached depth after hydration: the pre-hydration style tag
  // from _document.tsx lands before the globals.css stylesheet, whose stock
  // `.dark` values would otherwise win the cascade. applyDepth re-appends the
  // tag to the end of <head>, after every stylesheet.
  useEffect(() => {
    applyDepth(readStoredDepth());
  }, []);
  useEffect(() => {
    if (!data) return;
    if (data.theme && THEME_VALUES.has(data.theme) && data.theme !== theme) {
      setTheme(data.theme);
    }
    if (data.dark_mode_depth !== null && clampDepth(data.dark_mode_depth) !== readStoredDepth()) {
      storeDepth(data.dark_mode_depth);
    }
    // Reconcile only when fresh server data arrives, not on every local
    // theme change afterwards.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);
  return null;
}